import { useCallback, useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useLingui } from '@lingui/react/macro';
import { Loader2, type LucideIcon } from 'lucide-react';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useAuthContext } from '@/contexts/AuthContext';
import { BackNavigationContext } from '@/contexts/BackNavigationContext';
import { useCommandPalette } from '@/contexts/CommandPaletteContext';
import { useCanOpen } from '@/hooks/useCanOpen';
import {
  RECORD_ICONS,
  RECORD_SEARCH_MIN_LENGTH,
  recordPath,
  useCommandPaletteGuide,
  useCommandPaletteItems,
  useCommandPaletteRecords,
  type GuidePaletteItem,
  type PaletteItem,
  type RecordItem,
  type RecordType,
} from '@/hooks/useCommandPaletteItems';
import {
  readRecentEntries,
  recentEntryKey,
  recentStorageKey,
  writeRecentEntry,
  type RecentEntry,
} from './commandPaletteRecents';

/** Case- and accent-insensitive form: "zurich" finds "Zürich", "creche" finds "Crèche". */
function normalize(value: string): string {
  return value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}

/** True when the letters of `word` appear in `text` in order ("grup" finds "groups"). */
function isSubsequence(word: string, text: string): boolean {
  let at = 0;
  for (const char of word) {
    at = text.indexOf(char, at);
    if (at === -1) return false;
    at += 1;
  }
  return true;
}

/**
 * How well `item` answers `query`: every typed word must appear in the label,
 * the group or a keyword. Label hits outrank group and keyword hits; 0 means
 * no match.
 */
function matchScore(item: Pick<PaletteItem, 'label' | 'group' | 'keywords'>, words: readonly string[]): number {
  const label = normalize(item.label);
  const rest = normalize([item.group ?? '', ...item.keywords].join(' '));
  let score = 0;
  for (const word of words) {
    if (label.startsWith(word)) score += 3;
    else if (label.includes(word)) score += 2;
    else if (rest.includes(word)) score += 1;
    // A typo or a skipped letter still finds the page, ranked last.
    else if (word.length >= 3 && isSubsequence(word, label)) score += 0.5;
    else return 0;
  }
  return score;
}

function filterItems<T extends Pick<PaletteItem, 'label' | 'group' | 'keywords'>>(
  items: readonly T[],
  words: readonly string[],
): T[] {
  if (words.length === 0) return [...items];
  return items
    .map((item, index) => ({ item, index, score: matchScore(item, words) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.item);
}

/**
 * The last opened palette targets of the signed-in user, newest first. Kept in
 * sessionStorage under the org and user id, so they end with the tab, a
 * colleague never sees them, and sign-out clears them
 * (`clearRecentPaletteItems`). Records keep their name only, no detail line.
 */
function useRecentEntries(): [RecentEntry[], (entry: RecentEntry) => void] {
  const { orgId, userId } = useAuthContext();
  const key = recentStorageKey(orgId, userId);
  const [entries, setEntries] = useState<RecentEntry[]>(() => readRecentEntries(key));

  // Written at once, not in a state updater: the palette closes (and this
  // component unmounts) in the same step.
  const remember = useCallback((entry: RecentEntry) => setEntries(writeRecentEntry(key, entry)), [key]);

  return [entries, remember];
}

interface RowProps {
  value: string;
  icon: LucideIcon;
  label: string;
  secondary?: string;
  trailing?: string;
  onSelect: () => void;
}

function PaletteRow({ value, icon: Icon, label, secondary, trailing, onSelect }: RowProps) {
  return (
    <CommandItem value={value} onSelect={onSelect} className="gap-3 py-2">
      <Icon className="text-muted-foreground" aria-hidden="true" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{label}</span>
        {secondary ? <span className="truncate text-xs text-muted-foreground">{secondary}</span> : null}
      </span>
      {trailing ? <span className="shrink-0 text-xs text-muted-foreground">{trailing}</span> : null}
    </CommandItem>
  );
}

/** A guide hit row: the page, task, question or term, where it sits, the guide section. */
function GuideRow({ item, onSelect }: { item: GuidePaletteItem; onSelect: (path: string) => void }) {
  return (
    <PaletteRow
      value={item.id}
      icon={item.icon}
      label={item.label}
      secondary={item.secondary}
      trailing={item.group}
      onSelect={() => onSelect(item.path)}
    />
  );
}

function CommandPaletteBody() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { close } = useCommandPalette();
  // The shell's guard of a dirty page form; outside the shell there is none.
  const leaveGuard = useContext(BackNavigationContext)?.leaveGuard ?? null;
  const canOpen = useCanOpen();
  const { pages, settings } = useCommandPaletteItems();
  const searchGuide = useCommandPaletteGuide();
  const [query, setQuery] = useState('');
  const records = useCommandPaletteRecords(query);
  const [recent, remember] = useRecentEntries();

  const words = useMemo(() => normalize(query).split(/\s+/).filter(Boolean), [query]);
  const visiblePages = useMemo(() => filterItems(pages, words), [pages, words]);
  const visibleSettings = useMemo(() => filterItems(settings, words), [settings, words]);
  const showRecords = records.enabled && query.trim().length >= RECORD_SEARCH_MIN_LENGTH;
  // Guide hits answer a typed query only, after everything else.
  const visibleGuide = useMemo(() => (words.length > 0 ? searchGuide(query) : []), [searchGuide, query, words.length]);

  const recordTypeLabel = (recordType: RecordType): string => {
    switch (recordType) {
      case 'child':
        return t({ id: 'commandPalette.recordType.child', message: 'Child' });
      case 'contact':
        return t({ id: 'commandPalette.recordType.contact', message: 'Contact' });
      case 'employee':
        return t({ id: 'commandPalette.recordType.employee', message: 'Employee' });
    }
  };

  const recordSecondary = (record: Pick<RecordItem, 'recordType' | 'detail'>): string => {
    const type = recordTypeLabel(record.recordType);
    const detail = record.detail;
    return detail ? t({ id: 'commandPalette.record.secondary', message: `${type} · ${detail}` }) : type;
  };

  // Pages and settings resolve against the current lists, so a recent entry
  // follows the locale and disappears when the user lost access to it.
  const recentRows = useMemo(() => {
    if (words.length > 0) return [];
    const byId = new Map([...pages, ...settings].map((item) => [item.id, item]));
    return recent.flatMap((entry): RecentRow[] => {
      if (entry.type === 'page' || entry.type === 'setting') {
        const item = byId.get(recentEntryKey(entry));
        return item ? [{ kind: 'page', item }] : [];
      }
      const path = recordPath(entry.type, entry.id);
      return canOpen(path) && entry.name ? [{ kind: 'record', entry, path, recordType: entry.type }] : [];
    });
  }, [recent, pages, settings, words.length, canOpen]);

  // Leaving through the palette is a navigation like Back: a dirty form asks first.
  const go = (entry: RecentEntry, path: string) => {
    close();
    const leave = () => {
      remember(entry);
      navigate(path);
    };
    if (leaveGuard) leaveGuard(leave);
    else leave();
  };

  const goToItem = (item: PaletteItem) => go({ type: item.kind, id: item.path }, item.path);

  // A guide hit opens its topic at the best matching heading; Escape on the
  // guide page returns. Guide topics are not kept as recent items.
  const goToGuide = (path: string) => {
    close();
    const leave = () => navigate(path, { state: { fromPalette: true } });
    if (leaveGuard) leaveGuard(leave);
    else leave();
  };

  const nothingFound =
    visiblePages.length === 0 &&
    visibleSettings.length === 0 &&
    visibleGuide.length === 0 &&
    (!showRecords || (records.records.length === 0 && !records.isSearching));

  const placeholder = t({ id: 'commandPalette.placeholder', message: 'Search pages, settings and records' });

  return (
    <Command
      shouldFilter={false}
      loop
      // cmdk names the input through its own visually hidden <label>.
      label={placeholder}
      className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-input-wrapper]_svg]:h-5 [&_[cmdk-input-wrapper]_svg]:w-5 [&_[cmdk-input]]:h-12"
    >
      <CommandInput value={query} onValueChange={setQuery} placeholder={placeholder} />
      <CommandList label={t({ id: 'commandPalette.results', message: 'Results' })}>
        {nothingFound ? (
          <CommandEmpty>{t({ id: 'common.empty.noResults', message: 'No results found' })}</CommandEmpty>
        ) : null}

        {recentRows.length > 0 ? (
          <CommandGroup heading={t({ id: 'commandPalette.group.recent', message: 'Recent' })}>
            {recentRows.map((row) =>
              row.kind === 'page' ? (
                <PaletteRow
                  key={row.item.id}
                  value={`recent:${row.item.id}`}
                  icon={row.item.icon}
                  label={row.item.label}
                  trailing={row.item.group}
                  onSelect={() => goToItem(row.item)}
                />
              ) : (
                <PaletteRow
                  key={recentEntryKey(row.entry)}
                  value={`recent:${recentEntryKey(row.entry)}`}
                  icon={RECORD_ICONS[row.recordType]}
                  label={row.entry.name ?? ''}
                  secondary={recordTypeLabel(row.recordType)}
                  onSelect={() => go(row.entry, row.path)}
                />
              ),
            )}
          </CommandGroup>
        ) : null}

        {visiblePages.length > 0 ? (
          <CommandGroup heading={t({ id: 'commandPalette.group.pages', message: 'Pages' })}>
            {visiblePages.map((item) => (
              <PaletteRow
                key={item.id}
                value={item.id}
                icon={item.icon}
                label={item.label}
                trailing={item.group}
                onSelect={() => goToItem(item)}
              />
            ))}
          </CommandGroup>
        ) : null}

        {visibleSettings.length > 0 ? (
          <CommandGroup heading={t({ id: 'settings.title', message: 'Settings' })}>
            {visibleSettings.map((item) => (
              <PaletteRow
                key={item.id}
                value={item.id}
                icon={item.icon}
                label={item.label}
                trailing={item.group}
                onSelect={() => goToItem(item)}
              />
            ))}
          </CommandGroup>
        ) : null}

        {showRecords && (records.records.length > 0 || records.isSearching || records.isError) ? (
          <CommandGroup heading={t({ id: 'commandPalette.group.records', message: 'Records' })}>
            {records.records.map((record) => (
              <PaletteRow
                key={record.id}
                value={record.id}
                icon={RECORD_ICONS[record.recordType]}
                label={record.label}
                secondary={recordSecondary(record)}
                onSelect={() =>
                  go({ type: record.recordType, id: record.recordId, name: record.label }, record.path)
                }
              />
            ))}
            <div role="status" aria-live="polite" className="px-2 text-xs text-muted-foreground">
              {records.isSearching ? (
                <span className="flex items-center gap-2 py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                  {t({ id: 'commandPalette.records.searching', message: 'Searching records...' })}
                </span>
              ) : records.isError ? (
                <span className="block py-2 text-destructive">
                  {t({ id: 'commandPalette.records.error', message: 'Some records could not be searched. Try again.' })}
                </span>
              ) : null}
            </div>
          </CommandGroup>
        ) : null}

        {visibleGuide.length > 0 ? (
          <CommandGroup heading={t({ id: 'commandPalette.group.guide', message: 'Guide' })}>
            {visibleGuide.map((item) => (
              <GuideRow key={item.id} item={item} onSelect={goToGuide} />
            ))}
          </CommandGroup>
        ) : null}
      </CommandList>
    </Command>
  );
}

/**
 * The command palette (Cmd/Ctrl+K): jump to any page, settings page or
 * record by typing. Shows only what the session can open. Mount once inside
 * `CommandPaletteProvider`; the body (and its searches) exists only while the
 * dialog is open, so every opening starts with an empty query.
 */
/** A recent-items row: a page or setting resolved against the live lists, or a record. */
type RecentRow =
  | { kind: 'page'; item: PaletteItem }
  | { kind: 'record'; entry: RecentEntry; path: string; recordType: RecordType };

export function CommandPalette() {
  const { t } = useLingui();
  const { isOpen, setOpen } = useCommandPalette();

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent size="md" hideCloseButton className="gap-0 overflow-hidden p-0" aria-describedby={undefined}>
        <DialogTitle className="sr-only">{t({ id: 'commandPalette.title', message: 'Search' })}</DialogTitle>
        <CommandPaletteBody />
      </DialogContent>
    </Dialog>
  );
}
